import { useState, useCallback, useRef } from "react";
import type { MachineEvent } from "@/machines/types/event";

interface UseEventMoveProps {
  svgRef: React.RefObject<SVGSVGElement>;
  scaledWidth: number;
  min: number;
  max: number;
  snapToBoundary?: (timestamp: number, events: any[], type: 'start' | 'end') => number | null;
  machineEvents?: any[];
}

interface MoveState {
  event: MachineEvent;
  originalStart: number;
  originalEnd: number;
  duration: number;
  initialX: number;
}

export const useEventMove = ({
  svgRef,
  scaledWidth,
  min,
  max,
  snapToBoundary,
  machineEvents = [],
}: UseEventMoveProps) => {
  const [moveState, setMoveState] = useState<MoveState | null>(null);
  const [tempEventTimes, setTempEventTimes] = useState<{
    start: number;
    end: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const DRAG_THRESHOLD = 3; // pixels - minimum movement to count as drag

  // Use ref to always have the latest tempEventTimes value
  const tempEventTimesRef = useRef<{ start: number; end: number } | null>(null);

  const handleMoveStart = useCallback(
    (event: MachineEvent, clientX: number) => {
      const rect = svgRef.current?.getBoundingClientRect();
      if (!rect) return;

      const startTime = new Date(event.startTime).getTime();
      const endTime = new Date(event.endTime).getTime();
      const duration = endTime - startTime;

      const initialTimes = {
        start: startTime,
        end: endTime,
      };

      setMoveState({
        event,
        originalStart: startTime,
        originalEnd: endTime,
        duration,
        initialX: clientX,
      });

      setTempEventTimes(initialTimes);
      tempEventTimesRef.current = initialTimes;

      setIsDragging(false); // Reset dragging state
    },
    [svgRef]
  );

  const handleMoveMove = useCallback(
    (e: MouseEvent) => {
      if (!moveState || !svgRef.current) return;

      const deltaX = e.clientX - moveState.initialX;

      // Check if we've moved past the drag threshold
      if (!isDragging && Math.abs(deltaX) < DRAG_THRESHOLD) {
        return; // Not dragging yet, ignore small movements
      }

      if (!isDragging) {
        setIsDragging(true); // Start actual dragging
      }

      const rect = svgRef.current.getBoundingClientRect();
      const deltaTime = (deltaX / rect.width) * (max - min);

      let newStart = moveState.originalStart + deltaTime;
      let newEnd = moveState.originalEnd + deltaTime;

      // Apply snapping to start boundary if function provided
      if (snapToBoundary) {
        const snappedStart = snapToBoundary(newStart, machineEvents, 'start');
        if (snappedStart !== null) {
          // Snap start and adjust end to maintain duration
          newStart = snappedStart;
          newEnd = newStart + moveState.duration;
        } else {
          // Try snapping to end boundary
          const snappedEnd = snapToBoundary(newEnd, machineEvents, 'end');
          if (snappedEnd !== null) {
            // Snap end and adjust start to maintain duration
            newEnd = snappedEnd;
            newStart = newEnd - moveState.duration;
          }
        }
      }

      // Constrain to timeline bounds
      if (newStart < min) {
        newStart = min;
        newEnd = newStart + moveState.duration;
      }
      if (newEnd > max) {
        newEnd = max;
        newStart = newEnd - moveState.duration;
      }

      const newTimes = { start: newStart, end: newEnd };

      // Update both state and ref
      setTempEventTimes(newTimes);
      tempEventTimesRef.current = newTimes;
    },
    [moveState, svgRef, min, max, isDragging, DRAG_THRESHOLD, snapToBoundary, machineEvents]
  );

  const handleMoveEnd = useCallback(() => {
    // Use ref to get the latest tempEventTimes value
    const latestTempEventTimes = tempEventTimesRef.current;

    if (!moveState || !latestTempEventTimes) {
      setMoveState(null);
      setTempEventTimes(null);
      tempEventTimesRef.current = null;
      setIsDragging(false);
      return;
    }

    // Only return result if actually dragged (not just clicked)
    let result = null;
    if (isDragging) {
      result = {
        event: moveState.event,
        newStart: latestTempEventTimes.start,
        newEnd: latestTempEventTimes.end,
      };
    }

    setMoveState(null);
    setTempEventTimes(null);
    tempEventTimesRef.current = null;
    setIsDragging(false);

    return result;
  }, [moveState, isDragging]);

  const cancelMove = useCallback(() => {
    setMoveState(null);
    setTempEventTimes(null);
    setIsDragging(false);
  }, []);

  return {
    isMoving: !!moveState && isDragging, // Only count as moving if actually dragging
    movingEvent: moveState?.event || null,
    tempEventTimes,
    handleMoveStart,
    handleMoveMove,
    handleMoveEnd,
    cancelMove,
  };
};
